import { useState, useCallback, useMemo } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useToast } from '@/components/ToastContainer'
import { Button, Alert, Spinner, StatCard, Input } from '@/ui'
import { cn } from '@/ui'
import styles from './DbReset.module.css'

// ── 타입 ──

interface TableInfo {
  name: string
  count: number
}

interface GroupInfo {
  name: string
  label: string
  tables: TableInfo[]
}

interface PreAction {
  type: string
  table: string
  column: string
  targetTable: string
}

interface FkError {
  table: string
  column: string
  targetTable: string
  count: number
  requiredGroup: string
  message: string
}

interface GroupsResponse {
  groups: GroupInfo[]
}

interface PreviewResponse {
  groups: GroupInfo[]
  preActions: PreAction[]
  errors: FkError[]
}

interface ExecuteResponse {
  resetTables: number
  totalRowsDeleted: number
  preActionsExecuted: number
}

// ── 컴포넌트 ──

export function DbReset() {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [showConfirm, setShowConfirm] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [result, setResult] = useState<ExecuteResponse | null>(null)

  const { data: groupsData, isLoading, error, refetch } = useApiFetch<GroupsResponse>('/api/admin/db-reset/groups')
  const { trigger: fetchPreview, isLoading: previewLoading } = useApiMutation<PreviewResponse, never>(
    '/api/admin/db-reset/preview',
    'GET',
  )
  const { trigger: executeReset, isLoading: executing } = useApiMutation<ExecuteResponse, { tables: string[] }>(
    '/api/admin/db-reset/execute',
    'POST',
  )
  const { addToast } = useToast()

  const allTableNames = useMemo(() => {
    if (!groupsData) return []
    return groupsData.groups.flatMap((g) => g.tables.map((t) => t.name))
  }, [groupsData])

  const clearResults = useCallback(() => {
    setPreview(null)
    setResult(null)
  }, [])

  const toggleTable = useCallback((tableName: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(tableName)) next.delete(tableName)
      else next.add(tableName)
      return next
    })
    clearResults()
  }, [clearResults])

  const toggleGroup = useCallback((group: GroupInfo) => {
    setSelected((prev) => {
      const next = new Set(prev)
      const groupTableNames = group.tables.map((t) => t.name)
      const allSelected = groupTableNames.every((t) => next.has(t))
      for (const t of groupTableNames) {
        if (allSelected) next.delete(t)
        else next.add(t)
      }
      return next
    })
    clearResults()
  }, [clearResults])

  const selectAll = useCallback(() => {
    setSelected((prev) => (prev.size === allTableNames.length ? new Set() : new Set(allTableNames)))
    clearResults()
  }, [allTableNames, clearResults])

  const handlePreview = useCallback(async () => {
    if (selected.size === 0) return
    const tablesParam = Array.from(selected).join(',')
    const data = await fetchPreview(undefined, {
      pathOverride: `/api/admin/db-reset/preview?tables=${encodeURIComponent(tablesParam)}`,
    })
    if (data) setPreview(data)
  }, [selected, fetchPreview])

  const handleExecute = useCallback(async () => {
    if (confirmText !== 'RESET') {
      addToast({ type: 'error', message: 'RESET을 정확히 입력하세요' })
      return
    }
    const data = await executeReset({ tables: Array.from(selected) })
    if (data) {
      setResult(data)
      setShowConfirm(false)
      setConfirmText('')
      setPreview(null)
      addToast({ type: 'success', message: `${data.resetTables}개 테이블 초기화 완료 (${data.totalRowsDeleted}건 삭제)` })
      refetch()
    } else {
      addToast({ type: 'error', message: '초기화 실패' })
    }
  }, [confirmText, selected, executeReset, addToast, refetch])

  if (isLoading) return <Spinner />
  if (error) return <Alert variant="danger">{error}</Alert>
  if (!groupsData) return null

  const groups = groupsData.groups
  const hasErrors = preview && preview.errors.length > 0

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>DB 초기화</h1>
        <div className={styles.actions}>
          <Button variant="secondary" size="sm" onClick={selectAll}>
            {selected.size === groups.length ? '전체 해제' : '전체 선택'}
          </Button>
          <Button variant="secondary" size="sm" onClick={handlePreview} disabled={selected.size === 0} loading={previewLoading}>
            미리보기
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setShowConfirm(true)
              setConfirmText('')
            }}
            disabled={selected.size === 0 || !preview || !!hasErrors}
          >
            초기화 실행
          </Button>
        </div>
      </div>

      {/* 그룹 카드 */}
      <div className={styles.groups}>
        {groups.map((group) => {
          const groupTableNames = group.tables.map((t) => t.name)
          const selectedCount = groupTableNames.filter((t) => selected.has(t)).length
          const allSelected = selectedCount === groupTableNames.length
          const someSelected = selectedCount > 0 && !allSelected

          return (
            <div
              key={group.name}
              className={cn(styles.groupCard, allSelected && styles.groupCardSelected, someSelected && styles.groupCardPartial)}
            >
              <div className={styles.groupHeader}>
                <input
                  type="checkbox"
                  className={styles.groupCheckbox}
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected }}
                  onChange={() => toggleGroup(group)}
                />
                <span className={styles.groupName}>{group.label}</span>
                <span className={styles.groupCount}>{selectedCount}/{groupTableNames.length}</span>
              </div>
              <div className={styles.tableList}>
                {group.tables.map((t) => (
                  <label key={t.name} className={cn(styles.tableItem, selected.has(t.name) && styles.tableItemSelected)}>
                    <input
                      type="checkbox"
                      className={styles.tableCheckbox}
                      checked={selected.has(t.name)}
                      onChange={() => toggleTable(t.name)}
                    />
                    <span className={styles.tableName}>{t.name}</span>
                    <span className={styles.tableCount}>{t.count}</span>
                  </label>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* 미리보기 결과 */}
      {preview && (
        <div className={styles.previewSection}>
          <div className={styles.previewTitle}>미리보기</div>

          <table className={styles.previewTable}>
            <thead>
              <tr>
                <th>그룹</th>
                <th>테이블</th>
                <th className={styles.countCell}>건수</th>
              </tr>
            </thead>
            <tbody>
              {preview.groups.map((g) =>
                g.tables.map((t, i) => (
                  <tr key={t.name}>
                    {i === 0 && <td rowSpan={g.tables.length}>{g.label}</td>}
                    <td>{t.name}</td>
                    <td className={styles.countCell}>{t.count}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>

          {preview.preActions.length > 0 && (
            <div className={styles.preActionList}>
              <strong>사전 처리 (자동):</strong>
              {preview.preActions.map((a, i) => (
                <div key={i} className={styles.preActionItem}>
                  {a.table}.{a.column} → NULL (참조: {a.targetTable})
                </div>
              ))}
            </div>
          )}

          {preview.errors.length > 0 && (
            <div className={styles.errorList}>
              {preview.errors.map((e, i) => (
                <div key={i} className={styles.errorItem}>
                  {e.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 확인 다이얼로그 */}
      {showConfirm && (
        <div className={styles.previewSection}>
          <div className={styles.previewTitle}>초기화 확인</div>
          <Alert variant="warning">
            선택한 {selected.size}개 테이블의 데이터가 영구적으로 삭제됩니다. 되돌릴 수 없습니다.
          </Alert>
          <div className={styles.confirmInput}>
            <Input
              label="확인을 위해 RESET을 입력하세요"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="RESET"
            />
          </div>
          <div className={styles.actions} style={{ marginTop: 'var(--space-3)' }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowConfirm(false)
                setConfirmText('')
              }}
              disabled={executing}
            >
              취소
            </Button>
            <Button variant="danger" size="sm" onClick={handleExecute} loading={executing} disabled={confirmText !== 'RESET'}>
              초기화 실행
            </Button>
          </div>
        </div>
      )}

      {/* 결과 */}
      {result && (
        <div className={styles.resultSummary}>
          <StatCard label="초기화된 테이블" value={result.resetTables} />
          <StatCard label="삭제된 행" value={result.totalRowsDeleted} />
          <StatCard label="사전 처리" value={result.preActionsExecuted} />
        </div>
      )}
    </div>
  )
}
